// Appearance resolution — the pure logic the cookie pre-paint and the P1
// hydration both follow (Brief 4 / P0b). Kept here, unit-tested once, because
// the FOUC-kill rule must be exactly the same in two places: the inline
// pre-paint script in `index.html` (vanilla JS — it runs before the bundle, so
// it can't import this) MIRRORS `parseAppearanceCookie`, and the P1 app uses
// these functions directly. No platform globals here — it takes the cookie
// string in, so it ports to RN unchanged.

import type { Appearance } from "./types";

/** The cookie the appearance pre-paint READS and the preference store WRITES.
 * The cookie is the pre-paint cache; `GET /preferences` + the `preferences` SSE
 * kind are the authoritative, cross-surface source. */
export const APPEARANCE_COOKIE = "wt_appearance";

/** The washed theme family — the full set of `[data-appearance]` ids the cycle
 * walks, ordered light → dark. `light` (Newsprint) and `dark` (Carbon) keep their
 * canonical ids so the app's existing rules are untouched; the rest are the washed
 * papers. The display metadata (names / swatches) lives in modules/theme/ThemeCycle;
 * this is the authority for WHICH ids are valid. */
export const APPEARANCE_THEME_IDS = [
  "light", "iris", "basil", "ledger", "rhubarb", "fog", "cyan", "flamingo",
  "driftwood", "goldenrod", "pollen", "mimeo", "bubblegum", "melon", "kraft",
  "chambray", "riso", "punch", "sepia", "pistachio", "rust", "patina", "slate",
  "pondweed", "blueprint", "drydock", "petrol", "oxblood", "holly", "plum",
  "indigo", "nebula", "ember", "dark", "matrix", "synth", "phosphor",
  "filament", "char", "pilot",
] as const;

/** Every value that stamps a `[data-appearance]`: the 40 washed themes (incl.
 * light/dark) + the high-contrast a11y appearance. `system`/unset/anything else
 * stamps nothing (follow the OS). */
const STAMPABLE: ReadonlySet<string> = new Set([
  ...APPEARANCE_THEME_IDS,
  "high-contrast",
]);

/** Whether `v` is a valid appearance value (incl. `system`). */
export function isAppearance(v: string): v is Appearance {
  return v === "system" || STAMPABLE.has(v);
}

/** The `[data-appearance]` attribute value for a saved appearance, or `null` when
 * the page must follow the system (`system` / unset / invalid → no attribute, so
 * the token CSS's `prefers-color-scheme` decides). This IS the FOUC-kill rule: a
 * known appearance/theme id stamps the attribute pre-paint; everything else stamps
 * nothing. */
export function appearanceAttr(
  saved: string | null | undefined,
): Appearance | null {
  return saved && STAMPABLE.has(saved) ? (saved as Appearance) : null;
}

/** Parse the `wt_appearance` value out of a Cookie header string (the pre-paint
 * reads `document.cookie`; this is the same parse, made testable) and apply the
 * stamp rule. Returns the attribute to stamp, or `null` for system/unset/
 * invalid. The inline pre-paint script mirrors this exactly. */
export function parseAppearanceCookie(cookie: string): Appearance | null {
  const m = cookie.match(/(?:^|;\s*)wt_appearance=([^;]*)/);
  if (!m) return null;
  let v: string;
  try {
    v = decodeURIComponent(m[1]);
  } catch {
    v = m[1];
  }
  return appearanceAttr(v);
}

// --- write side + toggle cycle (P1 prep) ---------------------------------
// Pure helpers the P1 appearance hook will consume (the hook itself + the CSS
// wiring are the morning's coherent unit). Kept here so the
// writer mirrors the reader (`parseAppearanceCookie`) and the cycle order lives
// in ONE place — both unit-tested, no DOM, no CSS dependency.

/** The `document.cookie` string that persists `a` for the pre-paint to read:
 * the same `wt_appearance` key `parseAppearanceCookie` reads, `path=/` (so every
 * route's pre-paint sees it), a one-year max-age, `SameSite=Lax`. `system` is
 * stored explicitly and resolves to "no attribute" on read. Pure — the caller
 * assigns the result to `document.cookie`. */
export function appearanceCookie(a: Appearance): string {
  return `${APPEARANCE_COOKIE}=${encodeURIComponent(a)}; path=/; max-age=31536000; samesite=lax`;
}

/** The next appearance in the toggle cycle: system → light → dark →
 * high-contrast → system. Pure, so the cycle order is single-sourced for the
 * toggle control the morning hook renders. */
export function nextAppearance(current: Appearance): Appearance {
  const order: Appearance[] = ["system", "light", "dark", "high-contrast"];
  const i = order.indexOf(current);
  return order[(i + 1) % order.length] ?? "system";
}
